import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getFattureEmesse, refreshFatturaSdiStatus, reinviaFattura, exportFattureEmesseExcel } from '../services/api';
import { Table, Form, Spinner, Alert, Row, Col, Pagination, Card, Button, Badge, Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useMediaQuery } from 'react-responsive';
import MobileListLayout from '../common/MobileListLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSyncAlt, faFileInvoice, faFileExcel,
  faCheckCircle, faClock, faTimesCircle, faExclamationTriangle,
  faExternalLinkAlt, faBuilding, faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import SortableHeader from '../common/SortableHeader';
import { buildServerFilters } from '../common/serverFilterUtils';

const columnHelper = createColumnHelper();

const STATI_REINVIABILI = new Set(['BOZZ', 'ERRO', null, undefined, '']);

const SDI_CONFIG = {
  INVI: { label: 'Inviato',          bg: 'primary',   icon: faClock },
  INVA: { label: 'Inviato',          bg: 'primary',   icon: faClock },
  PREN: { label: 'In elaborazione',  bg: 'warning',   icon: faClock },
  CONS: { label: 'Consegnata',       bg: 'success',   icon: faCheckCircle },
  NONC: { label: 'Non consegnata',   bg: 'danger',    icon: faTimesCircle },
  ERRO: { label: 'Errore',           bg: 'danger',    icon: faExclamationTriangle },
  RIFI: { label: 'Rifiutata',        bg: 'danger',    icon: faTimesCircle },
  BOZZ: { label: 'Bozza',           bg: 'secondary', icon: faClock },
};

const TIPO_DOC_CONFIG = {
  FATTURA:      { label: 'Fattura',      bg: 'primary' },
  NOTA_CREDITO: { label: 'Nota Credito', bg: 'warning' },
  RICEVUTA:     { label: 'Ricevuta',     bg: 'info' },
};

const SDI_STATO_OPTIONS = [
  { value: '', label: 'Tutti gli stati SDI' },
  { value: 'INVI', label: 'Inviato' },
  { value: 'PREN', label: 'In elaborazione' },
  { value: 'CONS', label: 'Consegnata' },
  { value: 'NONC', label: 'Non consegnata' },
  { value: 'ERRO', label: 'Errore' },
  { value: 'RIFI', label: 'Rifiutata' },
  { value: 'BOZZ', label: 'Bozza' },
];

const TIPO_DOC_OPTIONS = [
  { value: '', label: 'Tutti i tipi' },
  { value: 'FATTURA', label: 'Fattura' },
  { value: 'NOTA_CREDITO', label: 'Nota Credito' },
  { value: 'RICEVUTA', label: 'Ricevuta' },
];

const SDI_STATO_FILTER_OPTIONS = SDI_STATO_OPTIONS.filter(o => o.value);
const TIPO_DOC_FILTER_OPTIONS = TIPO_DOC_OPTIONS.filter(o => o.value);

const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
const formatCurrency = (v) => `€ ${parseFloat(v || 0).toFixed(2)}`;

function getNrFattura(fattura) {
  return getNumeroDocumento(fattura.raw_response_data);
}

function getClienteDisplay(fattura) {
  const c = fattura.ordine?.cliente;
  if (!c) return '—';
  return c.denominazione_sociale || [c.nome, c.cognome].filter(Boolean).join(' ') || '—';
}

const STATI_ERRORE = new Set(['ERRO', 'RIFI']);
const CAMPI_XML = ['sdi_fattura_xml', 'sdi_file_notifica'];

// sdi_messaggio arriva come "[ListaErrori:Errore:Codice]\n00305\n[...:Descrizione]\n..." : coppie etichetta/valore
function parseSdiMessaggio(msg) {
  if (!msg) return [];
  const out = [];
  let label = null;
  for (const line of msg.split('\n').map(l => l.trim()).filter(Boolean)) {
    const m = line.match(/^\[(.+)\]$/);
    if (m) label = m[1].split(':').pop();
    else out.push({ label: label || '', value: line });
  }
  return out;
}

function getNumeroDocumento(raw) {
  return raw?.nostro_numero || raw?.numero_documento || raw?.dati_documento?.documento?.Numero
    || raw?.documento?.Numero || raw?.Numero || null;
}

function SdiBadge({ stato }) {
  const key = stato?.toUpperCase();
  const cfg = SDI_CONFIG[key] || { label: stato || 'N/D', bg: 'secondary', icon: faClock };
  return (
    <Badge bg={cfg.bg}>
      <FontAwesomeIcon icon={cfg.icon} className="me-1" />
      {cfg.label}
    </Badge>
  );
}

function TipoDocBadge({ tipo }) {
  const cfg = TIPO_DOC_CONFIG[tipo] || { label: tipo || 'N/D', bg: 'secondary' };
  return <Badge bg={cfg.bg}>{cfg.label}</Badge>;
}

function FatturaEmessaDetailModal({ fattura, onClose, onRefreshSdi, onReinvia }) {
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const [reinviando, setReinviando] = useState(false);
  const nr = getNrFattura(fattura) || fattura.fattura24_id;
  const cliente = getClienteDisplay(fattura);

  const raw = fattura.raw_response_data || {};
  const doc = raw.documento || {};
  const righe = useMemo(() => {
    const det = doc.DettaglioLinee || doc.dettaglio_linee || [];
    return Array.isArray(det) ? det : [det];
  }, [doc]);
  const datiTotali = doc.DatiRiepilogo || doc.dati_riepilogo || [];
  const riepilogo = Array.isArray(datiTotali) ? datiTotali : [datiTotali];
  const statoUp = fattura.sdi_stato?.toUpperCase();
  const inErrore = STATI_ERRORE.has(statoUp);
  const diagnostica = parseSdiMessaggio(raw.sdi_messaggio);
  const rawSenzaXml = useMemo(() => {
    const r = { ...raw };
    CAMPI_XML.forEach(k => { if (r[k]) r[k] = `(${r[k].length} caratteri XML omessi)`; });
    return r;
  }, [raw]);
  const [showRaw, setShowRaw] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefreshSdi(fattura.id);
    } finally {
      setRefreshing(false);
    }
  };

  const handleReinviaModal = async () => {
    setReinviando(true);
    try {
      await onReinvia(fattura.id);
      onClose();
    } finally {
      setReinviando(false);
    }
  };

  return (
    <Modal show onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faFileInvoice} className="me-2 text-primary" />
          Fattura {nr}
          <span className="ms-2"><SdiBadge stato={fattura.sdi_stato} /></span>
          <span className="ms-2"><TipoDocBadge tipo={fattura.tipo_documento} /></span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {inErrore && (
          <Alert variant="danger">
            <strong>Scartata dallo SDI</strong>
            {diagnostica.length > 0 ? (
              <ul className="mb-0 mt-1">
                {diagnostica.map((d, i) => (
                  <li key={i}>{d.label && <small className="text-muted">{d.label}: </small>}{d.value}</li>
                ))}
              </ul>
            ) : (
              <div className="mt-1">
                Nessun messaggio nella risposta salvata: premi <em>Aggiorna stato SDI</em> per scaricarla da Fattura24.
              </div>
            )}
            <div className="mt-2 small">
              Correggi i dati del cliente (P.IVA / codice fiscale / codice destinatario) e premi <em>Reinvia fattura</em>:
              viene rimandata con lo stesso numero.
            </div>
          </Alert>
        )}
        {statoUp === 'NONC' && (
          <Alert variant="info" className="small">
            La fattura è valida e consegnata nel cassetto fiscale del cliente (area riservata Agenzia Entrate):
            non va reinviata. Se serve, mandagli una copia di cortesia.
          </Alert>
        )}
        <Row className="mb-3">
          <Col md={4}><small className="text-muted d-block">Nr. Fattura</small><strong>{nr}</strong></Col>
          <Col md={4}><small className="text-muted d-block">Data</small>{formatDate(fattura.data_creazione)}</Col>
          <Col md={4}><small className="text-muted d-block">SDI ID Trasmissione</small>{fattura.sdi_identificativo || '—'}</Col>
        </Row>
        <Row className="mb-3">
          <Col md={6}>
            <small className="text-muted d-block"><FontAwesomeIcon icon={faBuilding} className="me-1" />Cliente</small>
            <strong>{cliente}</strong>
            {fattura.ordine?.cliente?.partita_iva && <div><small className="text-muted">P.IVA: {fattura.ordine.cliente.partita_iva}</small></div>}
            {fattura.ordine?.cliente?.codice_fiscale && <div><small className="text-muted">CF: {fattura.ordine.cliente.codice_fiscale}</small></div>}
          </Col>
          <Col md={3}>
            <small className="text-muted d-block">Ordine collegato</small>
            {fattura.ordine_id ? (
              <Button variant="link" size="sm" className="p-0"
                onClick={() => { onClose(); navigate(`/ordini/${fattura.ordine_id}`); }}>
                <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />#{fattura.ordine_id}
              </Button>
            ) : '—'}
          </Col>
          <Col md={3}>
            <small className="text-muted d-block fw-bold">Totale ordine</small>
            <span style={{ fontSize: '1.2rem', color: '#0d6efd', fontWeight: 'bold' }}>
              {fattura.ordine?.totale_ordine ? formatCurrency(fattura.ordine.totale_ordine) : '—'}
            </span>
          </Col>
        </Row>

        {righe.length > 0 && righe[0] && (
          <>
            <h6 className="mt-3 mb-2">Righe della fattura</h6>
            <Table size="sm" bordered>
              <thead><tr><th>Descrizione</th><th className="text-end">Qtà</th><th className="text-end">Prezzo unit.</th><th className="text-end">IVA</th><th className="text-end">Importo</th></tr></thead>
              <tbody>
                {righe.map((r, i) => (
                  <tr key={i}>
                    <td>{r.Descrizione || r.descrizione || '—'}</td>
                    <td className="text-end">{r.Quantita || r.quantita || '—'}</td>
                    <td className="text-end">{(r.PrezzoUnitario || r.prezzo_unitario) ? `€ ${parseFloat(r.PrezzoUnitario || r.prezzo_unitario).toFixed(2)}` : '—'}</td>
                    <td className="text-end">{(r.AliquotaIVA || r.aliquota_iva) ? `${r.AliquotaIVA || r.aliquota_iva}%` : '—'}</td>
                    <td className="text-end">{(r.PrezzoTotale || r.prezzo_totale) ? `€ ${parseFloat(r.PrezzoTotale || r.prezzo_totale).toFixed(2)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}

        {riepilogo.length > 0 && riepilogo[0] && (
          <>
            <h6 className="mt-3 mb-2">Riepilogo IVA</h6>
            <Table size="sm" bordered>
              <thead><tr><th>Aliquota</th><th className="text-end">Imponibile</th><th className="text-end">Imposta</th></tr></thead>
              <tbody>
                {riepilogo.map((r, i) => (
                  <tr key={i}>
                    <td>{(r.AliquotaIVA || r.aliquota_iva) ? `${r.AliquotaIVA || r.aliquota_iva}%` : '—'}</td>
                    <td className="text-end">{(r.ImponibileImporto || r.imponibile_importo) ? `€ ${parseFloat(r.ImponibileImporto || r.imponibile_importo).toFixed(2)}` : '—'}</td>
                    <td className="text-end">{(r.Imposta || r.imposta) ? `€ ${parseFloat(r.Imposta || r.imposta).toFixed(2)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}

        <Button variant="link" size="sm" className="p-0 mt-2" onClick={() => setShowRaw(s => !s)}>
          {showRaw ? 'Nascondi' : 'Mostra'} risposta completa Fattura24
        </Button>
        {showRaw && (
          <pre className="bg-light border rounded p-2 mt-2 small" style={{ maxHeight: 300, overflow: 'auto' }}>
            {JSON.stringify(rawSenzaXml, null, 2)}
          </pre>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Chiudi</Button>
        {STATI_REINVIABILI.has(fattura.sdi_stato) && (
          <Button variant="warning" onClick={handleReinviaModal} disabled={reinviando}>
            {reinviando
              ? <><Spinner size="sm" animation="border" className="me-1" />Reinvio...</>
              : <><FontAwesomeIcon icon={faPaperPlane} className="me-1" />Reinvia fattura</>}
          </Button>
        )}
        <Button variant="outline-primary" onClick={handleRefresh} disabled={refreshing}>
          {refreshing
            ? <><Spinner size="sm" animation="border" className="me-1" />Aggiornamento...</>
            : <><FontAwesomeIcon icon={faSyncAlt} className="me-1" />Aggiorna stato SDI</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function FattureEmesseList() {
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const navigate = useNavigate();

  const [fatture, setFatture] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFattura, setSelectedFattura] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [reinviandoId, setReinviandoId] = useState(null);

  const [totaleFatturato, setTotaleFatturato] = useState(0);
  const [sdiCounts, setSdiCounts] = useState({});
  const [totalFatture, setTotalFatture] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sorting, setSorting] = useState(() => {
    const saved = sessionStorage.getItem('list-sorting-fatture-emesse');
    return saved ? JSON.parse(saved) : [];
  });

  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = sessionStorage.getItem('list-filters-fatture-emesse');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    sessionStorage.setItem('list-sorting-fatture-emesse', JSON.stringify(sorting));
  }, [sorting]);

  useEffect(() => {
    sessionStorage.setItem('list-filters-fatture-emesse', JSON.stringify(columnFilters));
  }, [columnFilters]);

  // Selezione multipla
  const [selectedIds, setSelectedIds] = useState([]);

  const buildFilters = useCallback(
    () => JSON.stringify(buildServerFilters(columnFilters)),
    [columnFilters]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sortParams = sorting.map(s => `${s.id}:${s.desc ? 'desc' : 'asc'}`);
      const res = await getFattureEmesse((currentPage - 1) * itemsPerPage, itemsPerPage, sortParams, buildFilters());
      setFatture(res.data.fatture);
      setTotalFatture(res.data.total);
      setTotaleFatturato(res.data.totale_fatturato || 0);
      setSdiCounts(res.data.sdi_counts || {});
    } catch {
      setError('Errore nel caricamento delle fatture emesse.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, sorting, buildFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRefreshSdi = useCallback(async (fatturaId) => {
    setRefreshingId(fatturaId);
    try {
      const res = await refreshFatturaSdiStatus(fatturaId);
      setFatture(prev => prev.map(f => f.id === fatturaId ? res.data : f));
      if (selectedFattura?.id === fatturaId) setSelectedFattura(res.data);
      toast.success('Stato SDI aggiornato');
    } catch (err) {
      toast.error(`Errore: ${err.response?.data?.detail || err.message}`);
    } finally {
      setRefreshingId(null);
    }
  }, [selectedFattura]);

  const handleReinvia = useCallback(async (fatturaId) => {
    setReinviandoId(fatturaId);
    try {
      const res = await reinviaFattura(fatturaId);
      setFatture(prev => prev.map(f => f.id === fatturaId ? res.data : f));
      if (selectedFattura?.id === fatturaId) setSelectedFattura(res.data);
      toast.success('Fattura reinviata con successo');
    } catch (err) {
      toast.error(`Errore reinvio: ${err.response?.data?.detail || err.message}`);
    } finally {
      setReinviandoId(null);
    }
  }, [selectedFattura]);

  const handleExportExcel = async () => {
    try {
      const res = await exportFattureEmesseExcel(buildFilters());
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fatture_emesse_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error('Errore durante il download Excel');
    }
  };

  // Gestione selezione multipla
  const toggleSelectAll = () => {
    if (selectedIds.length === fatture.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(fatture.map(f => f.id));
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkRefreshSdi = async () => {
    if (selectedIds.length === 0) return;
    const promises = selectedIds.map(id => handleRefreshSdi(id));
    try {
      await Promise.all(promises);
      toast.success(`Aggiornati ${selectedIds.length} stati SDI`);
      setSelectedIds([]);
    } catch (err) {
      toast.error('Errore durante l\'aggiornamento bulk');
    }
  };

  const handleBulkExport = async () => {
    if (selectedIds.length === 0) return;
    try {
      const selectedFilters = { ids: selectedIds };
      const res = await exportFattureEmesseExcel(JSON.stringify(selectedFilters));
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fatture_selezionate_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSelectedIds([]);
    } catch {
      toast.error('Errore durante l\'export delle fatture selezionate');
    }
  };

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'select',
      header: () => (
        <Form.Check
          type="checkbox"
          checked={selectedIds.length === fatture.length && fatture.length > 0}
          onChange={toggleSelectAll}
        />
      ),
      enableSorting: false,
      enableColumnFilter: false,
      cell: info => (
        <Form.Check
          type="checkbox"
          checked={selectedIds.includes(info.row.original.id)}
          onChange={() => toggleSelectRow(info.row.original.id)}
          onClick={e => e.stopPropagation()}
        />
      ),
    }),
    columnHelper.display({
      id: 'nr_fattura',
      header: 'Nr. Fattura',
      enableSorting: false,
      enableColumnFilter: false,
      cell: info => <span className="fw-bold">{getNrFattura(info.row.original) || info.row.original.fattura24_id}</span>,
    }),
    columnHelper.accessor('tipo_documento', {
      header: 'Tipo',
      meta: { filterVariant: 'select', filterOptions: TIPO_DOC_FILTER_OPTIONS },
      cell: info => <TipoDocBadge tipo={info.getValue()} />,
    }),
    columnHelper.accessor('data_creazione', {
      header: 'Data',
      meta: { filterVariant: 'date-range' },
      cell: info => formatDate(info.getValue()),
    }),
    columnHelper.display({
      id: 'cliente_denominazione',
      header: 'Cliente',
      meta: { filterVariant: 'text' },
      cell: info => getClienteDisplay(info.row.original),
    }),
    columnHelper.display({
      id: 'ordine',
      header: 'N. Ordine',
      enableSorting: false,
      enableColumnFilter: false,
      cell: info => {
        const oid = info.row.original.ordine_id;
        return oid ? (
          <Button variant="link" size="sm" className="p-0"
            onClick={e => { e.stopPropagation(); navigate(`/ordini/${oid}`); }}>
            #{oid}
          </Button>
        ) : '—';
      },
    }),
    columnHelper.display({
      id: 'totale',
      header: 'Totale',
      enableSorting: false,
      enableColumnFilter: false,
      cell: info => {
        const t = info.row.original.ordine?.totale_ordine;
        return t ? formatCurrency(t) : '—';
      },
    }),
    columnHelper.accessor('sdi_stato', {
      header: 'Stato SDI',
      meta: { filterVariant: 'select', filterOptions: SDI_STATO_FILTER_OPTIONS },
      cell: info => <SdiBadge stato={info.getValue()} />,
    }),
    columnHelper.display({
      id: 'azioni',
      header: '',
      enableSorting: false,
      enableColumnFilter: false,
      cell: info => {
        const f = info.row.original;
        return (
          <div className="d-flex gap-1">
            {STATI_REINVIABILI.has(f.sdi_stato) && (
              <Button size="sm" variant="warning"
                onClick={e => { e.stopPropagation(); handleReinvia(f.id); }}
                disabled={reinviandoId === f.id}
                title="Reinvia fattura">
                <FontAwesomeIcon icon={faPaperPlane} spin={reinviandoId === f.id} />
              </Button>
            )}
            <Button size="sm" variant="outline-primary"
              onClick={e => { e.stopPropagation(); handleRefreshSdi(f.id); }}
              disabled={refreshingId === f.id}
              title="Aggiorna stato SDI">
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshingId === f.id} />
            </Button>
          </div>
        );
      },
    }),
  ], [selectedIds, fatture, refreshingId, reinviandoId, navigate, handleRefreshSdi, handleReinvia]);

  const table = useReactTable({
    data: fatture,
    columns,
    state: { sorting, columnFilters },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualFiltering: true,
    manualSorting: true,
  });

  const hasActiveFilters = columnFilters.length > 0;

  const resetFilters = () => {
    table.resetColumnFilters();
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalFatture / itemsPerPage);

  const renderPaginationItems = () => {
    if (totalPages <= 1) return null;
    const items = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);
    for (let i = start; i <= end; i++) {
      items.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>{i}</Pagination.Item>);
    }
    return items;
  };

  const renderCard = useCallback((fattura) => {
    const nr = getNrFattura(fattura) || fattura.fattura24_id;
    return (
      <div>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h6 className="mb-1"><FontAwesomeIcon icon={faFileInvoice} className="me-2 text-muted" />Fattura {nr}</h6>
            <small className="text-muted">{getClienteDisplay(fattura)}</small>
          </div>
          <SdiBadge stato={fattura.sdi_stato} />
        </div>
        <Row className="mb-2">
          <Col xs={6}>
            <small className="text-muted d-block">Totale</small>
            <strong style={{ color: '#0d6efd' }}>{fattura.ordine?.totale_ordine ? formatCurrency(fattura.ordine.totale_ordine) : '—'}</strong>
          </Col>
          <Col xs={6}>
            <small className="text-muted d-block">Data</small>
            {formatDate(fattura.data_creazione)}
          </Col>
        </Row>
        <Row className="mb-2">
          <Col xs={6}><TipoDocBadge tipo={fattura.tipo_documento} /></Col>
          <Col xs={6}>{fattura.ordine_id && <Badge bg="info">Ordine #{fattura.ordine_id}</Badge>}</Col>
        </Row>
        <div className="mt-2 d-flex gap-1">
          <Button size="sm" variant="outline-primary" onClick={() => setSelectedFattura(fattura)}>Dettaglio</Button>
          {STATI_REINVIABILI.has(fattura.sdi_stato) && (
            <Button size="sm" variant="warning" onClick={() => handleReinvia(fattura.id)} disabled={reinviandoId === fattura.id}>
              <FontAwesomeIcon icon={faPaperPlane} spin={reinviandoId === fattura.id} />
            </Button>
          )}
          <Button size="sm" variant="outline-secondary" onClick={() => handleRefreshSdi(fattura.id)} disabled={refreshingId === fattura.id}>
            <FontAwesomeIcon icon={faSyncAlt} spin={refreshingId === fattura.id} />
          </Button>
        </div>
      </div>
    );
  }, [refreshingId, reinviandoId, handleRefreshSdi, handleReinvia]);

  const consegnate = sdiCounts['CONS'] || 0;
  const inElaborazione = (sdiCounts['PREN'] || 0) + (sdiCounts['INVI'] || 0) + (sdiCounts['INVA'] || 0);
  const errori = (sdiCounts['ERRO'] || 0) + (sdiCounts['NONC'] || 0) + (sdiCounts['RIFI'] || 0);

  return (
    <>
      {selectedFattura && (
        <FatturaEmessaDetailModal
          fattura={selectedFattura}
          onClose={() => setSelectedFattura(null)}
          onRefreshSdi={handleRefreshSdi}
          onReinvia={handleReinvia}
        />
      )}

      <Row className="mb-3 align-items-center">
        <Col><h1 className="h3 mb-0">Fatture Emesse</h1></Col>
        <Col xs="auto" className="d-flex gap-2">
          {hasActiveFilters && (
            <Button variant="outline-secondary" onClick={resetFilters}>
              Azzera filtri <Badge bg="danger" className="ms-1">{columnFilters.length}</Badge>
            </Button>
          )}
          <Button variant="outline-success" onClick={handleExportExcel}>
            <FontAwesomeIcon icon={faFileExcel} className="me-1" />Excel
          </Button>
        </Col>
      </Row>

      {/* KPI */}
      <Row className="mb-3">
        <Col md={3}>
          <Card className="border-primary">
            <Card.Body className="py-2">
              <small className="text-muted d-block">Totale fatturato</small>
              <span className="fs-5 fw-bold text-primary">{formatCurrency(totaleFatturato)}</span>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-success">
            <Card.Body className="py-2">
              <small className="text-muted d-block">Consegnate SDI</small>
              <span className="fs-5 fw-bold text-success"><FontAwesomeIcon icon={faCheckCircle} className="me-1" />{consegnate}</span>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-warning">
            <Card.Body className="py-2">
              <small className="text-muted d-block">In elaborazione</small>
              <span className="fs-5 fw-bold text-warning"><FontAwesomeIcon icon={faClock} className="me-1" />{inElaborazione}</span>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className={errori > 0 ? 'border-danger' : ''}>
            <Card.Body className="py-2">
              <small className="text-muted d-block">Errori / Non consegnate</small>
              <span className={`fs-5 fw-bold ${errori > 0 ? 'text-danger' : 'text-muted'}`}>
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />{errori}
              </span>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Barra azioni bulk */}
      {selectedIds.length > 0 && !isMobile && (
        <Alert variant="info" className="mb-3 d-flex justify-content-between align-items-center">
          <span>
            <strong>{selectedIds.length}</strong> fattura/e selezionata/e
          </span>
          <div>
            <Button size="sm" variant="primary" onClick={handleBulkRefreshSdi} className="me-2">
              <FontAwesomeIcon icon={faSyncAlt} className="me-1" />
              Aggiorna SDI ({selectedIds.length})
            </Button>
            <Button size="sm" variant="success" onClick={handleBulkExport} className="me-2">
              <FontAwesomeIcon icon={faFileExcel} className="me-1" />
              Esporta selezionate
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setSelectedIds([])}>
              Deseleziona tutto
            </Button>
          </div>
        </Alert>
      )}

      <Card>
        <Card.Body className="p-0 p-md-3">
          {loading && <div className="text-center py-4"><Spinner animation="border" /></div>}
          {error && <Alert variant="danger" className="m-3">{error}</Alert>}
          {!loading && !error && (
            isMobile ? (
              <MobileListLayout
                items={fatture}
                renderCard={renderCard}
                entityName="fattura"
                showCreateButton={false}
                showSelectAll={false}
              />
            ) : (
              <div style={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
                <Table striped bordered hover responsive size="sm">
                  <thead>
                    {table.getHeaderGroups().map(hg => (
                      <tr key={hg.id}>
                        {hg.headers.map(header => (
                          <SortableHeader key={header.id} header={header} table={table} stickyColumns={[]} />
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.length === 0 ? (
                      <tr><td colSpan={9} className="text-center text-muted py-4">Nessuna fattura trovata</td></tr>
                    ) : (
                      table.getRowModel().rows.map(row => (
                        <tr key={row.id} style={{ cursor: 'pointer' }} onDoubleClick={() => setSelectedFattura(row.original)}>
                          {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )
          )}
        </Card.Body>
        <Card.Footer className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <Form.Group as={Row} className="align-items-center mb-0">
            <Col xs="auto"><Form.Label className="mb-0 small">Righe</Form.Label></Col>
            <Col xs="auto">
              <Form.Select size="sm" value={itemsPerPage} onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Form.Select>
            </Col>
            <Col xs="auto"><small className="text-muted">Totale: {totalFatture}</small></Col>
          </Form.Group>
          <Pagination size="sm" className="mb-0">
            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} />
            {renderPaginationItems()}
            <Pagination.Next onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages} />
            <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages} />
          </Pagination>
        </Card.Footer>
      </Card>
    </>
  );
}

export default FattureEmesseList;
